import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import {useDictionariesStore} from 'entities/dictionaries/index.ts';
import {useEffect, useRef, useState} from 'react';
import {toRem} from 'shared/helpers';
import {Drawer} from 'shared/ui/drawer';
import {AddLarge, ChevronRightIcon, CloseIcon} from 'shared/ui/icons';
import {useHudCollapse} from 'widgets/hud/model/collapseStore.ts';
import {useHudOverlay} from 'widgets/hud/model/overlayStore.ts';
import {SETTINGS_BUTTONS} from 'widgets/profile/lib/buttons.ts';
import {MobileSettings} from 'widgets/profile/ui/MobileSettings.tsx';
import {ThemeController} from 'widgets/profile/ui/SettingsSection/ThemeController.tsx';
import {ButtonContainer, HLine, TzItem, TzList} from 'widgets/profile/ui/styles.ts';
import {ProfileButton} from 'widgets/profile/ui/ui/Button.tsx';
import DictionariesContent from 'widgets/profile/ui/ui/DictionariesContent.tsx';
import {useOutClick} from '../../../app/lib/hooks/useOutClick.ts';
import {useResize} from '../../../app/lib/hooks/useResize.ts';
import {useUniqueDictionaries} from '../lib/useUniqueDictionaries.ts';
import {
  LeftSectionWrapper,
  RightSectionWrapper,
  SettingsControlsWrapper,
  SettingsDrawerWrapper,
  SettingsSectionContent,
  SettingsSectionTitle,
  SettingsTitle,
} from './styles.ts';
import {EditIcon} from 'shared/ui/icons/editIcon.tsx';
import {NAME_DICTIONARIES} from 'shared/consts/common.ts';
import {VetisContent} from './ui/VetisContent.tsx';
import {useTimezoneUpdate} from 'widgets/profile/lib/useTimezoneUpdate.ts';

type TSelectedSection =
  | 'themeSettings'
  | 'dictionarySettings'
  | 'vetisSection'
  | 'timezoneSettings';

export const SettingsModal = () => {
  const openId = useHudOverlay((s) => s.openId);
  const close = useHudOverlay((s) => s.close);
  const collapsed = useHudCollapse((s) => s.collapsed);

  const openAddModal = useDictionariesStore((s) => s.openAddModal);
  const dictionaries = useDictionariesStore((s) => s.dictionaries);
  const openEditModal = useDictionariesStore((s) => s.openEditModal);

  const {activeTz, timezones, handleTimezoneChange} = useTimezoneUpdate();

  const {selectedDictionary, uniqueDictionaries, setSelectedDictionary} = useUniqueDictionaries(
    dictionaries,
    false
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedSection, setSelectedSection] = useState<TSelectedSection | null>(null);

  const {isScreenMd} = useResize();

  useOutClick(containerRef, close);

  const isOpen = !!openId?.toLowerCase().includes('settings');

  useEffect(() => {
    if (!isOpen) return;
    if (isScreenMd) setSelectedSection('themeSettings');
    document.getElementById('mainNav')?.style.setProperty('z-index', '15'); // todo fix it
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
      document.getElementById('mainNav')?.style.setProperty('z-index', 'system'); // todo fix it
    };
  }, [isOpen, close, isScreenMd]);

  if (collapsed || !isOpen) return null;

  if (isScreenMd)
    return (
      <>
        <Drawer isOpen={isOpen} onClose={close} position="submenu">
          <SettingsDrawerWrapper>
            <LeftSectionWrapper>
              <SettingsTitle>Настройки</SettingsTitle>
              <SettingsControlsWrapper>
                {SETTINGS_BUTTONS.map((item) => (
                  <ProfileButton
                    isActive={item.id === selectedSection}
                    item={item}
                    key={item.id}
                    aria-label={item.label}
                    onClick={() => setSelectedSection(item.id as TSelectedSection)}
                  />
                ))}
              </SettingsControlsWrapper>
            </LeftSectionWrapper>
            {selectedSection === 'timezoneSettings' && (
              <RightSectionWrapper>
                <Button
                  iconPrefix={<CloseIcon />}
                  variant="secondary"
                  colorScheme="neutral"
                  style={{
                    alignSelf: 'flex-end',
                    width: toRem(24),
                    margin: `${toRem(4)} ${toRem(4)} 0 0`,
                    position: 'absolute',
                  }}
                  size="s"
                  onClick={close}
                />
                <SettingsSectionTitle>Часовой пояс</SettingsSectionTitle>
                <HLine />
                <TzList>
                  <TzItem
                    key={'Автоопределение'}
                    $active={null === activeTz}
                    onClick={() => handleTimezoneChange(null)}
                    type="button"
                  >
                    {'Автоопределение'}
                  </TzItem>
                  {timezones.map((it, ind) => (
                    <TzItem
                      key={ind}
                      $active={it.id === activeTz}
                      onClick={() => handleTimezoneChange(it.id)}
                      type="button"
                    >
                      {it.label}
                    </TzItem>
                  ))}
                </TzList>
              </RightSectionWrapper>
            )}
            {selectedSection === 'dictionarySettings' && (
              <LeftSectionWrapper>
                <SettingsTitle>Словари</SettingsTitle>
                {uniqueDictionaries.map((el) => {
                  return (
                    <ButtonContainer
                      key={el.id}
                      $active={selectedDictionary?.type === el.type}
                      $isMobileMenu={false}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => setSelectedDictionary(el)}
                      style={{width: '100%'}}
                    >
                      {NAME_DICTIONARIES[el.type] ?? el.type}
                      {!isScreenMd && <ChevronRightIcon />}
                      {!NAME_DICTIONARIES[el.type] && (
                        <div
                          className={'edit-button'}
                          onClick={() => openEditModal(el.id, 'dictionaryName')}
                          style={{marginLeft: 'auto'}}
                        >
                          <EditIcon />
                        </div>
                      )}
                    </ButtonContainer>
                  );
                })}

                <Button
                  label="Новый словарь"
                  colorScheme="neutral"
                  size="m"
                  variant="outline"
                  iconPrefix={<AddLarge fill={'#585D69'} />}
                  style={{
                    width: '100%',
                  }}
                  onClick={() => openAddModal()}
                />
              </LeftSectionWrapper>
            )}
            {(selectedSection === 'themeSettings' ||
              (selectedSection === 'dictionarySettings' && selectedDictionary)) && (
              <RightSectionWrapper>
                <Button
                  iconPrefix={<CloseIcon />}
                  variant="secondary"
                  colorScheme="neutral"
                  style={{
                    alignSelf: 'flex-end',
                    width: toRem(24),
                    margin: `${toRem(4)} ${toRem(4)} 0 0`,
                    position: 'absolute',
                  }}
                  size="s"
                  onClick={close}
                />
                {selectedSection === 'themeSettings' && (
                  <SettingsSectionContent>
                    <SettingsSectionTitle>Тема оформления</SettingsSectionTitle>
                    <HLine />
                    <ThemeController controllerType="toggler" />
                  </SettingsSectionContent>
                )}
                {selectedSection === 'dictionarySettings' && selectedDictionary && (
                  <DictionariesContent dictionaryType={selectedDictionary} />
                )}
              </RightSectionWrapper>
            )}
            {selectedSection === 'vetisSection' && (
              <RightSectionWrapper>
                <Button
                  iconPrefix={<CloseIcon />}
                  variant="secondary"
                  colorScheme="neutral"
                  style={{
                    alignSelf: 'flex-end',
                    width: toRem(24),
                    margin: `${toRem(4)} ${toRem(4)} 0 0`,
                    position: 'absolute',
                  }}
                  size="s"
                  onClick={close}
                />
                <SettingsSectionTitle>Интеграция с ФГИС</SettingsSectionTitle>
                <HLine />
                <VetisContent />
              </RightSectionWrapper>
            )}
          </SettingsDrawerWrapper>
        </Drawer>
      </>
    );

  return <MobileSettings />;
};
